import { EntityStatusChip } from '../../components/entity-detail/EntityStatusChip.js';
import { useI18nSubscription } from '../i18n/useI18nSubscription.js';
import { i18n } from '../../i18n/index.js';
// Три связанных поля привязки: компания, контакт, сделка. Порядок и каскад
// одинаковы во всех формах — сделка идёт последней, потому что её список
// зависит от выбранных компании и контакта.
import type { ReactElement } from 'react';
import { Form, Select } from 'antd';

import type { CompanyOption, EntityLinkCascade } from './useEntityLinkCascade.js';

interface EntityLinkFieldsProps {
  cascade: EntityLinkCascade;
  /** Форма открыта с карточки сущности: поле скрыто, значение проставлено жёстко. */
  lockedDealId?: string | undefined;
  lockedContactId?: string | undefined;
  lockedCompanyId?: string | undefined;
  /** Форма самой сделки: привязка к сделке бессмысленна, поле убираем. */
  hideDealField?: boolean;
}

export function EntityLinkFields({
  cascade,
  lockedDealId,
  lockedContactId,
  lockedCompanyId,
  hideDealField = false,
}: EntityLinkFieldsProps): ReactElement {
  useI18nSubscription();
  return (
    <>
      <div className="flex gap-3">
        {lockedCompanyId ? null : (
          <Form.Item
            name="companyId"
            label={i18n.t('crm.features.features.entity.links.entitylinkfields.компания')}
            className="flex-1"
            extra={cascade.companyHint}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={i18n.t(
                'crm.features.features.entity.links.entitylinkfields.без_компании',
              )}
              loading={cascade.companiesLoading}
              options={cascade.companyOptions}
              // Список компаний намеренно полный: фильтр по контакту запер бы смену
              // компании до сброса контакта. Вместо этого «чужие» помечены тегом —
              // в самом поле он не нужен, поэтому рисуем только в выпадающем списке.
              optionRender={(option) =>
                (option.data as CompanyOption).foreign ? (
                  <span className="flex items-center justify-between gap-2">
                    <span>{option.data.label}</span>
                    <EntityStatusChip tone="default" style={{ margin: 0 }}>
                      {i18n.t('crm.features.features.entity.links.entitylinkfields.другая')}
                    </EntityStatusChip>
                  </span>
                ) : (
                  option.data.label
                )
              }
              onChange={cascade.handleCompanyChange}
            />
          </Form.Item>
        )}
        {lockedContactId ? null : (
          <Form.Item
            name="contactId"
            label={i18n.t('crm.features.features.entity.links.entitylinkfields.контакт')}
            className="flex-1"
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={i18n.t(
                'crm.features.features.entity.links.entitylinkfields.без_контакта',
              )}
              loading={cascade.contactsLoading}
              options={cascade.contactOptions}
              // В самом поле — только имя: компания и так стоит в соседнем поле.
              // В списке вариантов подпись с компанией остаётся.
              labelRender={(item) => cascade.contactNameById(String(item.value))}
              onChange={cascade.handleContactChange}
            />
          </Form.Item>
        )}
      </div>

      {hideDealField || lockedDealId ? null : (
        <Form.Item
          name="dealId"
          label={i18n.t('crm.features.features.entity.links.entitylinkfields.сделка')}
          extra={cascade.dealHint}
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={
              cascade.dealOptions.length === 0
                ? i18n.t(
                    'crm.features.features.entity.links.entitylinkfields.подходящих_сделок_нет',
                  )
                : i18n.t('crm.features.features.entity.links.entitylinkfields.без_сделки')
            }
            options={cascade.dealOptions}
            onChange={cascade.handleDealChange}
          />
        </Form.Item>
      )}
    </>
  );
}
